import { TemplateResult, html, unsafeCSS } from 'lit';
import { state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { ContextConsumer } from '@lit/context';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafAlert } from '@cigna/dcws_leaf-web-components/components/alert/alert.js';
import { LeafButton } from '@cigna/dcws_leaf-web-components/components/button/button.component.js';
import { LeafInput } from '@cigna/dcws_leaf-web-components/components/input/input.component.js';
import { LeafLabel } from '@cigna/dcws_leaf-web-components/components/label/label.component.js';
import { LeafSelect } from '@cigna/dcws_leaf-web-components/components/select/select.component.js';
import { LeafSelectOption } from '@cigna/dcws_leaf-web-components/components/select-option/select-option.component.js';
import { LeafCheckbox } from '@cigna/dcws_leaf-web-components/components/checkbox/checkbox.component.js';
import { LeafCheckgroup } from '@cigna/dcws_leaf-web-components/components/checkgroup/checkgroup.component.js';
import { LeafSpinner } from '@cigna/dcws_leaf-web-components/components/spinner/spinner.component.js';
import { LeafValidationMessage } from '@cigna/dcws_leaf-web-components/components/validation-message/validation-message.component.js';
import { LeafIcon } from '@cigna/dcws_leaf-web-components/components/icon/icon.component.js';
import { CHCPhoneNumber } from '@cigna/dcws_leaf-web-components/chc-components/phone-number/phone-number.component.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import { endpointsContext } from '../contexts.js';
import { contactus } from './utils/connect-with-us-form-api.js';
import styles from './connect-with-us-form.scss?inline';
import type { connectWithUsFormLocale, submittedData } from './interface.js';

export class CHCconnectwithusform extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  static override styles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-button': LeafButton,
    'leaf-input': LeafInput,
    'leaf-label': LeafLabel,
    'leaf-select': LeafSelect,
    'leaf-select-option': LeafSelectOption,
    'leaf-spinner': LeafSpinner,
    'leaf-checkbox': LeafCheckbox,
    'leaf-checkgroup': LeafCheckgroup,
    'leaf-validation-message': LeafValidationMessage,
    'leaf-alert': LeafAlert,
    'leaf-icon': LeafIcon,
    'chc-phone-number': CHCPhoneNumber,
  };
  protected override ns = 'medicare-connectus-form';
  private connectWithUsFormLocale?: connectWithUsFormLocale;

  @state()
  showLoading = false;

  @state()
  connectWithUsEndpoint = '';

  @state()
  view = 1;

  override onNamespacesLoaded = () => {
    this.connectWithUsFormLocale = this.i18n?.getLanguageData(this.ns) as connectWithUsFormLocale;
  };

  constructor() {
    super();
    new ContextConsumer(this, {
      context: endpointsContext,
      callback: (endpoints) => {
        this.connectWithUsEndpoint = endpoints.medicareConnectUsForm;
      },
    });
  }

  connectFormSubmit = async (event: SubmitEvent) => {
    event.preventDefault();

    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const formDataObject = Object.fromEntries(formData) as unknown as submittedData;
    try {
      const response = await contactus(this.connectWithUsEndpoint, formDataObject);

      if (response === 200) {
        this.view = 2;
      } else {
        this.view = 3;
      }
    } catch (e) {
      this.view = 3;
    }
  };

  rendererror(): TemplateResult {
    return html`
      <p class="leadin">${ifDefined(this.connectWithUsFormLocale?.misc.leadInCopy)}</p>
      <leaf-alert variant="standalone" type="error" open>
        <h6 slot="heading">${this.connectWithUsFormLocale?.misc.errorAlertHeader}</h6>
        ${this.connectWithUsFormLocale?.misc.errorAlertBodyText}
      </leaf-alert>
    `;
  }

  rendersubmitted(): TemplateResult {
    return html`
      <p class="leadin">${ifDefined(this.connectWithUsFormLocale?.misc.leadInCopy)}</p>
      <leaf-alert variant="standalone" type="success" open>
        <h6 slot="heading">${this.connectWithUsFormLocale?.misc.successAlertHeader}</h6>
        ${this.connectWithUsFormLocale?.misc.successAlertBodyText}
      </leaf-alert>
    `;
  }

  renderForm(): TemplateResult {
    return html`
      <p class="leadin">${ifDefined(this.connectWithUsFormLocale?.misc.leadInCopy)}</p>
      <form name="connect-form" @submit=${this.connectFormSubmit}>
        <div class="form-body">
          <div class="form-col">
            <leaf-label for="connect-form-fname"
              >${ifDefined(this.connectWithUsFormLocale?.form.firstNameLabel)}</leaf-label
            >
            <leaf-input name="firstName" id="connect-form-fname" type="text" required> </leaf-input>
          </div>
          <div class="form-col">
            <leaf-label for="connect-form-lname"
              >${ifDefined(this.connectWithUsFormLocale?.form.lastNameLabel)}</leaf-label
            >
            <leaf-input name="lastName" id="connect-form-lname" type="text" required> </leaf-input>
          </div>
          <div class="form-col">
            <leaf-label for="connect-form-phone"
              >${ifDefined(this.connectWithUsFormLocale?.form.phoneNumberLabel)}</leaf-label
            >
            <leaf-input
              autocomplete="tel"
              pattern="[+]?[0-9- ]{0,14}$"
              name="phone"
              id="connect-form-phone"
              type="tel"
              required
            >
            </leaf-input>
            <span slot="note" id="connect-form-phone-note">+1 123 456 5678</span>
          </div>
          <div class="form-col">
            <leaf-label for="connect-form-language"
              >${ifDefined(this.connectWithUsFormLocale?.form.languagePrefLabel)}</leaf-label
            >
            <leaf-select name="langpref" id="connect-form-language" required>
              <leaf-select-option value disabled selected
                >${ifDefined(this.connectWithUsFormLocale?.form.defaultOption)}</leaf-select-option
              >
              <leaf-select-option value="english"
                >${ifDefined(this.connectWithUsFormLocale?.form.englishOption)}</leaf-select-option
              >
              <leaf-select-option value="spanish"
                >${ifDefined(this.connectWithUsFormLocale?.form.spanishOption)}</leaf-select-option
              >
            </leaf-select>
          </div>
        </div>

        <leaf-checkgroup required name="agree" id="leaf-checkgroup-id">
          <leaf-checkbox value="true">${ifDefined(this.connectWithUsFormLocale?.misc.agreeText)}</leaf-checkbox>
        </leaf-checkgroup>

        <leaf-spinner size="small" ?hidden=${!this.showLoading}></leaf-spinner>
        <leaf-button variant="primary" type="submit" ?disabled=${this.showLoading}
          >${ifDefined(this.connectWithUsFormLocale?.misc.submitBtnText)}</leaf-button
        >
      </form>
    `;
  }

  connectwithusRender(): TemplateResult {
    return html`
      <div ?hidden=${this.view !== 1}>${this.renderForm()}</div>
      <div ?hidden=${this.view !== 2}>${this.rendersubmitted()}</div>
      <div ?hidden=${this.view !== 3}>${this.rendererror()}</div>
    `;
  }

  override render() {
    const componentClassName = {
      'chcapp-connect-with-us-form': true,
    };
    const loadingOrError = this.renderLoadingOrError();
    if (loadingOrError) {
      return loadingOrError;
    }
    return html` <div class=${classMap(componentClassName)}><slot></slot> ${this.connectwithusRender()}</div>`;
  }
}
